import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HeroSection } from './components/HeroSection';
import { Navbar } from './components/Navbar';
import { VideoLibrary } from './components/VideoLibrary';
import { ResourcesSection } from './components/ResourcesSection';
import { Footer } from './components/Footer';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import { VideoLessons } from './pages/VideoLessons';
import { VideoDetailPage } from './pages/VideoDetailPage';
import { PracticeExercises } from './pages/PracticeExercises';
import { ExerciseDetail } from './pages/ExerciseDetail';
import { VocabularyLists } from './pages/VocabularyLists';
import { GrammarGuide } from './pages/GrammarGuide';
import { GrammarTopicPage } from './pages/GrammarTopicPage';
import { DiscussionForum } from './pages/DiscussionForum';
import { LearningPath } from './components/LearningPath';
import { ProfileSettings } from './pages/ProfileSettings';
import { Learn } from './pages/Learn';
import { Community } from './pages/Community';
import { LessonPage } from './pages/LessonPage';
import { Resources } from './pages/Resources';
import { PronunciationGuide } from './pages/PronunciationGuide';
import { BeginnersGuidePage } from './pages/BeginnersGuidePage';
import { BusinessEnglishPage } from './pages/BusinessEnglishPage';
import { Blog } from './pages/Blog';
import { RecommendedResources } from './pages/RecommendedResources';
import { ProfilePage } from './pages/ProfilePage';
import { PageTransition } from './components/PageTransition';
import { ToastContainer } from './components/ui/ToastContainer';
import './styles/animations.css';
import { ComingSoonPage } from './components/ComingSoonPage';
import { LearningUnitPage } from './pages/LearningUnitPage';
import { Course1Page } from './pages/Course1Page';
import { LearningDataProvider } from './context/LearningDataContext';
import { EducationalVideosPage } from './pages/EducationalVideosPage';
import { LearningPathPage } from './pages/LearningPathPage';
import { LessonOne } from './pages/LessonOne';
import { LessonThree } from './pages/LessonThree';
import { NumbersAndCounting } from './pages/NumbersAndCounting';
import { BasicGreetingsLesson } from './pages/lessons/BasicGreetingsLesson';
import { NumbersCountingLesson } from './pages/lessons/NumbersCountingLesson';
import { ErrorBoundary } from './components/ErrorBoundary';
function ProtectedRoute({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    isAuthenticated
  } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace data-id="element-0" />;
  }
  return <>{children}</>;
}
function HomePage() {
  return <>
      <HeroSection data-id="element-1" />
      <ResourcesSection data-id="element-2" />
      <VideoLibrary data-id="element-3" />
    </>;
}
function AppContent() {
  return <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 relative overflow-hidden transition-colors duration-300" data-id="element-4">
      <div className="absolute inset-0 bg-grid-slate-200/[0.02] dark:bg-grid-slate-50/[0.02] -z-1" data-id="element-5" />
      <div className="relative" data-id="element-6">
        <Navbar data-id="element-7" />
        <PageTransition data-id="element-8">
          <Routes data-id="element-9">
            <Route path="/" element={<HomePage data-id="element-11" />} data-id="element-10" />
            <Route path="/learn" data-id="element-12">
              <Route index element={<Learn data-id="element-14" />} data-id="element-13" />
              <Route path="path" element={<LearningPath data-id="element-16" />} data-id="element-15" />
              <Route path="path/full" element={<LearningPathPage data-id="element-18" />} data-id="element-17" />
              <Route path="unit/:unitId" element={<LearningUnitPage data-id="element-20" />} data-id="element-19" />
              <Route path="unit/:unitId/lesson/:lessonId" element={<LessonPage data-id="element-22" />} data-id="element-21" />
              <Route path="course/:courseId" element={<Course1Page data-id="element-24" />} data-id="element-23" />
              <Route path="course/:courseId/lesson/1" element={<LessonOne data-id="element-26" />} data-id="element-25" />
              <Route path="course/:courseId/lesson/2" element={<BasicGreetingsLesson data-id="element-28" />} data-id="element-27" />
              <Route path="course/:courseId/lesson/3" element={<NumbersCountingLesson data-id="element-30" />} data-id="element-29" />
              <Route path="course/:courseId/lesson/4" element={<LessonThree data-id="element-32" />} data-id="element-31" />
              <Route path="course/:courseId/lesson/5" element={<NumbersAndCounting data-id="element-34" />} data-id="element-33" />
              <Route path="video-lessons" data-id="element-35">
                <Route index element={<VideoLessons data-id="element-37" />} data-id="element-36" />
                <Route path=":videoId" element={<VideoDetailPage data-id="element-39" />} data-id="element-38" />
              </Route>
              <Route path="educational-videos" element={<EducationalVideosPage data-id="element-41" />} data-id="element-40" />
              <Route path="practice/*" data-id="element-42">
                <Route index element={<PracticeExercises data-id="element-44" />} data-id="element-43" />
                <Route path=":exerciseId" element={<ExerciseDetail data-id="element-46" />} data-id="element-45" />
              </Route>
              <Route path="vocabulary" element={<VocabularyLists data-id="element-48" />} data-id="element-47" />
            </Route>
            <Route path="/profile" element={<ProtectedRoute data-id="element-50">
                  <ProfilePage data-id="element-51" />
                </ProtectedRoute>} data-id="element-49" />
            <Route path="/profile/settings" element={<ProtectedRoute data-id="element-53">
                  <ProfileSettings data-id="element-54" />
                </ProtectedRoute>} data-id="element-52" />
            <Route path="/profile/activity" element={<ProtectedRoute data-id="element-56">
                  <ComingSoonPage data-id="element-57" />
                </ProtectedRoute>} data-id="element-55" />
            <Route path="/resources" data-id="element-58">
              <Route index element={<Resources data-id="element-60" />} data-id="element-59" />
              <Route path="grammar" data-id="element-61">
                <Route index element={<GrammarGuide data-id="element-63" />} data-id="element-62" />
                <Route path=":topicId" element={<GrammarTopicPage data-id="element-65" />} data-id="element-64" />
              </Route>
              <Route path="pronunciation" element={<PronunciationGuide data-id="element-67" />} data-id="element-66" />
              <Route path="beginners-guide/*" data-id="element-68">
                <Route index element={<BeginnersGuidePage data-id="element-70" />} data-id="element-69" />
                <Route path=":sectionId" element={<BeginnersGuidePage data-id="element-72" />} data-id="element-71" />
              </Route>
              <Route path="business-english" element={<BusinessEnglishPage data-id="element-74" />} data-id="element-73" />
              <Route path="recommended" element={<RecommendedResources data-id="element-76" />} data-id="element-75" />
            </Route>
            <Route path="/community" data-id="element-77">
              <Route index element={<Community data-id="element-79" />} data-id="element-78" />
              <Route path="discussions" element={<DiscussionForum data-id="element-81" />} data-id="element-80" />
            </Route>
            <Route path="/blog" data-id="element-82">
              <Route index element={<Blog data-id="element-84" />} data-id="element-83" />
            </Route>
            <Route path="*" element={<ComingSoonPage data-id="element-86" />} data-id="element-85" />
          </Routes>
        </PageTransition>
        <Footer data-id="element-87" />
      </div>
      <ToastContainer position="bottom-right" data-id="element-88" />
    </div>;
}
export function App() {
  return <BrowserRouter data-id="element-89">
      <ThemeProvider data-id="element-90">
        <LanguageProvider data-id="element-91">
          <ErrorBoundary data-id="element-92">
            <AuthProvider data-id="element-93">
              <LearningDataProvider data-id="element-94">
                <AppContent data-id="element-95" />
              </LearningDataProvider>
            </AuthProvider>
          </ErrorBoundary>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>;
}